import type { PaletteInput, Recipe, RouteArtifact } from './contracts';
import { resolvePalette, semanticScope } from './materials';
import { validateRecipe } from './validate';
import { buildDeadBracken, buildFallenLog } from './woodland';
import { buildMistShelf, buildTidePool } from './wetlands';
export { validateRecipe } from './validate';
export { resolvePalette } from './materials';
export type * from './contracts';

/** Pure browser-safe SVG construction. Build tooling adds SHA-256 provenance separately. */
export function generateRouteProp(input: unknown, overrides: PaletteInput = {}, density: 1 | 2 = 2, instanceScope = ''): RouteArtifact {
  const recipe: Recipe = validateRecipe(input);
  if (density !== 1 && density !== 2) throw new Error('Raster density must be 1 or 2');
  const palette = resolvePalette(recipe.paletteId, overrides), { width: w, height: h } = recipe.params;
  if (instanceScope && !/^[a-zA-Z][a-zA-Z0-9_-]{0,80}$/.test(instanceScope)) throw new Error('Invalid SVG instance scope');
  const id = `rp-${instanceScope ? instanceScope + '-' : ''}${semanticScope({ recipe, palette })}`;
  let body: string;
  switch (recipe.generatorId) {
  case 'route.fallen_log.v1': body = buildFallenLog(recipe.params, palette, recipe.seed, recipe.detail); break;
  case 'route.dead_bracken.v1': body = buildDeadBracken(recipe.params, palette, recipe.seed, recipe.detail); break;
  case 'route.tide_pool.v1': body = buildTidePool(recipe.params, palette, recipe.seed, recipe.detail, id); break;
  case 'route.mist_shelf.v1': body = buildMistShelf(recipe.params, palette, recipe.seed, recipe.detail, id); break;
  }
  const family = recipe.generatorId.slice(6, -3) as RouteArtifact['family'];
  const frame = { w, h }, point = { x: w / 2, y: h };
  const spans = family === 'fallen_log' ? [[22 / 250, 76 / 250], [180 / 250, 231 / 250]]
    : family === 'dead_bracken' ? [[23 / 76, 55 / 76]]
      : family === 'tide_pool' ? [[283 / 620, 337 / 620]] : [[30 / 300, 274 / 300]];
  const segments = spans.map(([left, right]) => ({ left: { x: w * left, y: h }, right: { x: w * right, y: h } }));
  return {
    classification: 'CANDIDATE_NOT_INTEGRATED', revision: 'route-props.1', recipe, family, frame,
    origin: { kind: 'bottom-center', point },
    // Conservative declared envelope; raster checks record the tighter occupied alpha box.
    contentBounds: { x: 0, y: 0, width: w, height: h }, paintBounds: { x: 0, y: 0, width: w, height: h },
    boundsPolicy: 'conservative-frame-envelope',
    anchors: [{ name: 'base', point, role: 'origin' },
      { name: 'frame_footprint_left', point: { x: 0, y: h }, role: 'inspection' },
      { name: 'frame_footprint_right', point: { x: w, y: h }, role: 'inspection' },
      ...segments.map((segment, i) => ({ name: `painted_contact_${i + 1}`, point: { x: (segment.left.x + segment.right.x) / 2, y: h }, role: 'support' as const }))],
    footprint: { left: { x: 0, y: h }, right: { x: w, y: h }, gameplayAuthority: false,
      meaning: 'conservative-current-validator-span-not-physical-support' },
    paintedContact: { segments, meaning: 'authored-visible-underside-not-collision' },
    renderRole: 'ground-dressing', gameplayGeometry: 'not-generated', palette, body,
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${Math.round(w * density)}" height="${Math.round(h * density)}" viewBox="0 0 ${w} ${h}">${body}</svg>`,
    recommendedRaster: { w: Math.round(w * 2), h: Math.round(h * 2) },
  };
}
